import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import pick from "lodash/pick";
import debounce from "lodash/debounce";
import { format } from "date-fns";
import { ResponsiveImage } from "@quintype/components";
import TrinityPlayer from "../../../atoms/TrinityPlayer";
import ListenButton from "../../../atoms/ListenButton";
import PrintIcon from "../../../../../assets/icons/printicon";
import ReadLaterIcon from "../../../../../assets/icons/Readlatericon";
import { STATIC_KEYWORDS } from "../../../../constants";
import { formatToMonthYear } from "../../../utils/date-formatter";
import { getLinkTarget } from "../../../../arrow/utils/utils";
import {
	createBookmark,
	getBookmarkByPageId,
	deleteBookmarkByPageId,
	generateJwtToken
} from "../../../../../api/utils";

import "./HeroSection.m.css";

const HeroSection = ({
	story,
	desktopSvg,
	sectionName,
	sectionSlug,
	entityName,
	firstPublishedAt,
	config,
	shouldShowHeadlineOnly,
	disableListenPrint = false
}) => {
	const heroImageKey = get(story, ["hero-image-s3-key"], "");
	const heroImageMetadata = get(story, ["hero-image-metadata"], {});
	const headline = get(story, ["headline"], "");
	const subheadline = get(story, ["subheadline"], "");
	const quarter = get(
		story,
		["metadata", "story-attributes", "quarter", 0],
		""
	);
	const metadata = get(story, ["metadata"], {});
	const expertName = get(metadata, "expert") || "";
	const expertDescription = get(metadata, "expert-description") || "";
	const [isPlayerVisible, setPlayerVisible] = useState(false);
	const [jwtToken, setJwtToken] = useState(null);
	const [isBookmarking, setIsBookmarking] = useState(false);
	const [isBookmarked, setIsBookmarked] = useState(false);
	const lastMemberIdRef = useRef(null);
	const member = useSelector((state) => get(state, ["member"], null));
	const metypeConfig = useSelector((state) =>
		get(state, ["qt", "config", "publisher-attributes", "metypeConfig"], "")
	);

	// Fetch JWT token when user logs in
	const fetchJwtToken = async () => {
		if (!metypeConfig?.integrationsId) return;

		try {
			const token = await generateJwtToken(metypeConfig);
			if (token) setJwtToken(token);
		} catch (error) {
			console.error("Error fetching JWT token:", error);
		}
	};

	// Initial JWT fetch
	useEffect(() => {
		fetchJwtToken();
	}, [metypeConfig]);

	// Refetch JWT when member changes (login/logout)
	useEffect(() => {
		if (member?.id && member?.id !== lastMemberIdRef.current) {
			lastMemberIdRef.current = member.id;
			fetchJwtToken();
		} else if (!member?.id) {
			setJwtToken(null);
			setIsBookmarked(false);
		}
	}, [member?.id, metypeConfig]);

	// Check if story is bookmarked on load
	useEffect(() => {
		const checkBookmarkStatus = async () => {
			if (!jwtToken || !story?.url) return;

			try {
				const bookmarkData = await getBookmarkByPageId(
					metypeConfig,
					story.url,
					jwtToken
				);
				setIsBookmarked(!!bookmarkData);
			} catch (error) {
				console.error("Error checking bookmark status:", error);
				setIsBookmarked(false);
			}
		};

		checkBookmarkStatus();
	}, [jwtToken, story?.url]);

	const handleReadLaterDebounced = useCallback(
		debounce(() => {
			if (!jwtToken) {
				console.warn("User not logged in. JWT token not available.");
				// Button is disabled via CSS.
				const currentUrl = new URL(window.location.href);
				const cleanUrl = currentUrl.origin + currentUrl.pathname;
				const returnUrl = encodeURIComponent(cleanUrl);
				window.location.href = `/user-login?url=${returnUrl}`;
				return;
			}

			if (isBookmarking) return;

			setIsBookmarking(true);

			if (isBookmarked) {
				// Delete bookmark
				deleteBookmarkByPageId(metypeConfig, story.url, jwtToken)
					.then(() => {
						setIsBookmarked(false);
					})
					.catch((error) => {
						console.error("Failed to remove article:", error);
					})
					.finally(() => {
						setIsBookmarking(false);
					});
			} else {
				// Create bookmark
				const tags = get(story, ["tags"], []);

				const stringifiedTags = JSON.stringify(tags);

				const storyObject = pick(story, [
					"id",
					"slug",
					"headline",
					"subheadline",
					"url",
					"published-at",
					"story-template",
					stringifiedTags
				]);
				const linkedEntity = get(story, ["linked-entities", 0], null);
				const desktopSvg = linkedEntity
					? get(linkedEntity, ["desktop-svg", 0, "url"], "")
					: "";

				const requestObj = {
					data: {
						type: "bookmark",
						attributes: {
							metadata: {
								...storyObject,
								sectionName: story.sections?.[0]?.["display-name"] || "",
								sectionUrl: story.sections?.[0]?.["section-url"] || "",
								domainSlug: story.sections?.[0]?.["domain-slug"] || "",
								"hero-image-s3-key":
									story.alternative?.home?.default?.["hero-image"]?.[
									"hero-image-s3-key"
									] || story["hero-image-s3-key"],
								"desktop-svg": desktopSvg
							},
							read_at: format(new Date(), "dd-MM-yyyy")
						}
					}
				};

				createBookmark(metypeConfig, story.url, requestObj, jwtToken)
					.then(() => {
						setIsBookmarked(true);
					})
					.catch((error) => {
						console.error("Failed to save article:", error);
					})
					.finally(() => {
						setIsBookmarking(false);
					});
			}
		}, 300),
		[jwtToken, story, isBookmarked, isBookmarking]
	);

	const handleReadLater = () => {
		handleReadLaterDebounced();
	};

	const fallbackImage = useSelector((state) =>
		get(state, ["qt", "config", "publisher-attributes", "fallback-image"], "")
	);

	const allowedAuthorIds = useSelector((state) =>
		get(
			state,
			["qt", "config", "publisher-attributes", "allowed-author-ids"],
			[]
		)
	);

	const handleListenClick = () => {
		setPlayerVisible(true);
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: "dl_story_interactions",
				event_action: "listen_click",
				platform: window.innerWidth >= 768 ? "desktop" : "mobile"
			});
		}
	};

	const handlePrint = () => {
		window.print();
	};

	const authors = get(story, ["authors"], []);

	const filteredAuthors = authors.filter((author) =>
		allowedAuthorIds.includes(author["author-id"] || author["id"])
	);

	const authorName = filteredAuthors.map((a) => a.name).join(", ");

	const formattedDate = formatToMonthYear(firstPublishedAt);
	const hasHeroImage = !!heroImageKey;
	const { PRINT, READ_LATER } = STATIC_KEYWORDS;

	const storyTemplate = get(story, ["story-template"], "");
	const isLeadershipFocus = storyTemplate === "leadership-in-focus";

	return (
		<div
			styleName={`hero-section ${isLeadershipFocus ? "leadership-in-focus-page" : ""
				}`}
			className={`hero-section ${isLeadershipFocus ? "leadership-in-focus-page" : ""
				}`}
		>
			{/* Hero Top Section */}
			{!shouldShowHeadlineOnly && (
				<div styleName="hero-wrapper" className="hero-wrapper">
					<div
						styleName={`red-overlay ${isLeadershipFocus ? "leadership-overlay" : ""
							}`}
						className={`red-overlay ${isLeadershipFocus ? "leadership-overlay" : ""
							}`}
					>
						<div styleName="overlay-text">
							{isLeadershipFocus ? (
								<div styleName="leadership-text">{sectionName}</div>
							) : (
								<>
									{desktopSvg && (
										<div styleName="report-icon">
											<img src={desktopSvg} alt="Report icon" />
										</div>
									)}
									{entityName && (
										<div styleName="entity-name">
											<span styleName="entity-name-link">{entityName}</span>
										</div>
									)}
								</>
							)}
						</div>
					</div>

					<div
						styleName={`hero-image-wrapper ${isLeadershipFocus ? "leadershipImageWrapper" : ""
							}`.trim()}
						className="hero-image-wrapper"
					>
						{hasHeroImage ? (
							<ResponsiveImage
								slug={heroImageKey}
								metadata={heroImageMetadata}
								aspectRatio={[16, 9]}
								defaultWidth={1280}
								widths={[1280, 1920]}
								imgParams={{ auto: ["format", "compress"] }}
								eager="above-fold"
								alt={headline || "Hero Image"}
								styleName="hero-image"
							/>
						) : (
							<img
								src={fallbackImage}
								alt={headline || "Hero Image"}
								styleName="hero-image"
							/>
						)}
					</div>
				</div>
			)}

			<div styleName="hero-content">
				{sectionName && sectionSlug && !isLeadershipFocus && (
					<div styleName="section-name">
						<a
							href={`/${sectionSlug}`}
							styleName="entity-name-link"
							target={getLinkTarget(`/${sectionSlug}`)}
							rel="noopener"
						>
							{sectionName}
						</a>
					</div>
				)}

				<div className="trinity-content-wrapper trinity-read" data-trinity-content="true">
					<h1
						styleName={`headline ${isLeadershipFocus ? "leadership-headline" : ""
							}`}
						className="headline"
					>
						{headline}
					</h1>
					{isLeadershipFocus && (expertName || expertDescription) && (
						<div styleName="expert-info-wrapper">
							{expertName && (
								<span styleName="expert-name">
									<strong>{expertName}</strong>
								</span>
							)}
							{expertName && expertDescription && (
								<span styleName="expert-description">&nbsp;</span>
							)}
							{expertDescription && (
								<span styleName="expert-description">
									<strong>{expertDescription}</strong>
								</span>
							)}
						</div>
					)}
					{!shouldShowHeadlineOnly && subheadline && (
						<h2
							styleName={
								isLeadershipFocus ? "leadership-subheadline" : "subheadline"
							}
							className="subheadline"
						>
							{subheadline}
						</h2>
					)}
				</div>
				{isLeadershipFocus && <div styleName="intro-text-wrapper"></div>}

				{!shouldShowHeadlineOnly && (
					<div styleName="meta-and-actions">
						<div styleName="publication-meta">
							{quarter && <span>{quarter}</span>}
							{quarter && formattedDate && <span styleName="separator">|</span>}
							{formattedDate && <span>{formattedDate}</span>}
							{authorName && <span styleName="separator">|</span>}
							{authorName && <span>{authorName}</span>}
						</div>

						<div styleName="action-buttons">
							<ListenButton styleName="action-btn" onClick={handleListenClick} />
							<button
								styleName={`action-btn${disableListenPrint ? " action-btn-disabled" : ""
									}`}
								onClick={() => !disableListenPrint && handlePrint()}
								disabled={disableListenPrint}
							>
								<PrintIcon
									width={24}
									height={24}
									color={disableListenPrint ? "#BFBFBF" : "currentColor"}
								/>
								<span>{PRINT}</span>
							</button>
							<button
								styleName={!jwtToken ? "action-btn disabled" : "action-btn"}
								onClick={handleReadLater}
								disabled={isBookmarking || !jwtToken}
							>
								<ReadLaterIcon
									width={24}
									height={24}
									color={isBookmarked ? "none" : "#242424"}
									fill={isBookmarked ? "#ff2626" : "none"}
								/>
								<span>{READ_LATER}</span>
							</button>
						</div>
					</div>
				)}
				<div styleName="leadership-divider" className="leadershipDivider" />
				{isPlayerVisible && <TrinityPlayer storyUrl={story.url} />}
			</div>
		</div>
	);
};

HeroSection.propTypes = {
	story: PropTypes.object.isRequired,
	desktopSvg: PropTypes.string,
	sectionName: PropTypes.string,
	sectionSlug: PropTypes.string,
	entityName: PropTypes.string,
	firstPublishedAt: PropTypes.number,
	config: PropTypes.object,
	shouldShowHeadlineOnly: PropTypes.bool,
	disableListenPrint: PropTypes.bool
};

export default HeroSection;